import { useState } from 'react';
import {
  Alert as MuiAlert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Edit, Widgets } from '@mui/icons-material';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as apps from '@/api/applications';
import type { DeveloperApplication } from '@/api/applications';
import { toApiError } from '@/api/client';
import { ApiProductRouteGuard } from '@/components/ApiProductRouteGuard';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingScreen';
import { EnvironmentBadge } from '@/components/primitives';
import { formatDate } from '@/lib/formatters';

export const Route = createFileRoute('/_authenticated/aplicaciones/')({
  component: AplicacionesPage,
});

type Toast = { message: string; severity: 'success' | 'error' };

function AplicacionesPage(): JSX.Element {
  return (
    <ApiProductRouteGuard>
      <AplicacionesContent />
    </ApiProductRouteGuard>
  );
}

function AplicacionesContent(): JSX.Element {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<Toast | null>(null);
  const [editing, setEditing] = useState<DeveloperApplication | 'new' | null>(null);

  const listQ = useQuery({ queryKey: ['developer-applications'], queryFn: () => apps.listDeveloperApplications(0, 100) });
  const invalidate = (): void => void queryClient.invalidateQueries({ queryKey: ['developer-applications'] });
  const onError = (err: unknown): void => setToast({ message: toApiError(err).message, severity: 'error' });

  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) => apps.setDeveloperApplicationEnabled(id, enabled),
    onSuccess: () => invalidate(),
    onError,
  });

  const items = listQ.data?.items ?? [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Widgets color="primary" /> Aplicaciones
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Agrupan la integración de un consumidor de la API (credenciales, webhooks, ambiente).
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => setEditing('new')}>Nueva aplicación</Button>
      </Stack>

      {listQ.isLoading ? (
        <LoadingScreen message="Cargando aplicaciones…" />
      ) : listQ.error ? (
        <MuiAlert severity="error">{toApiError(listQ.error).message}</MuiAlert>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<Widgets fontSize="inherit" />}
          title="Sin aplicaciones"
          description="Creá una aplicación para organizar la integración de un sistema externo con la API."
          actionLabel="Nueva aplicación"
          onAction={() => setEditing('new')}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Ambiente</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Creada</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.name}</Typography>
                    {a.description && <Typography variant="caption" color="text.secondary">{a.description}</Typography>}
                  </TableCell>
                  <TableCell><EnvironmentBadge environment={a.environment} /></TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" color={a.status === 'Active' ? 'success' : 'default'} label={a.status === 'Active' ? 'Activa' : 'Deshabilitada'} />
                  </TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatDate(a.createdAt)}</Typography></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <Tooltip title={a.status === 'Active' ? 'Deshabilitar' : 'Activar'}>
                        <Switch size="small" checked={a.status === 'Active'} disabled={toggleMut.isPending} onChange={(e) => toggleMut.mutate({ id: a.id, enabled: e.target.checked })} />
                      </Tooltip>
                      <Tooltip title="Editar"><IconButton size="small" onClick={() => setEditing(a)}><Edit fontSize="small" /></IconButton></Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editing && (
        <AppDialog
          app={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
            setToast({ message: 'Aplicación guardada.', severity: 'success' });
          }}
          onError={onError}
        />
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <MuiAlert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}

function AppDialog({
  app,
  onClose,
  onSaved,
  onError,
}: {
  app: DeveloperApplication | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}): JSX.Element {
  const isNew = app === null;
  const [name, setName] = useState(app?.name ?? '');
  const [description, setDescription] = useState(app?.description ?? '');
  const [environment, setEnvironment] = useState<'Sandbox' | 'Production'>(app?.environment ?? 'Sandbox');

  const mut = useMutation({
    mutationFn: () => {
      const input = { name: name.trim(), description: description.trim() || null };
      return isNew
        ? apps.createDeveloperApplication({ ...input, environment })
        : apps.updateDeveloperApplication(app!.id, input);
    },
    onSuccess: onSaved,
    onError,
  });

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isNew ? 'Nueva aplicación' : 'Editar aplicación'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField label="Nombre" fullWidth value={name} onChange={(e) => setName(e.target.value)} placeholder="Integración ERP" autoFocus />
          <TextField
            select
            label="Ambiente"
            fullWidth
            value={environment}
            disabled={!isNew}
            onChange={(e) => setEnvironment(e.target.value as 'Sandbox' | 'Production')}
            helperText={isNew
              ? 'El ambiente queda fijado al crear la aplicación y define qué rutas puede consumir.'
              : 'El ambiente de una aplicación no se puede cambiar.'}
          >
            <MenuItem value="Sandbox">Sandbox — simulación sin efectos fiscales</MenuItem>
            <MenuItem value="Production">Production — operaciones fiscales reales</MenuItem>
          </TextField>
          {environment === 'Production' && (
            <MuiAlert severity="warning">
              Las credenciales de esta aplicación deberán vincularse a un CUIT productivo.
            </MuiAlert>
          )}
          <TextField label="Descripción (opcional)" fullWidth multiline minRows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" disabled={name.trim().length === 0 || mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? <CircularProgress size={18} /> : isNew ? 'Crear' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
